import React from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Approximate coordinates for each venue.
const venues = [
  { name: "Skyline Golf", region: "Delhi NCR", lat: 28.5355, lng: 77.391 },
  { name: "Zen Golf", region: "Delhi NCR", lat: 28.4595, lng: 77.0266 },
  { name: "Golden Greens", region: "Delhi NCR", lat: 28.4845, lng: 77.0731 },
  { name: "Classic Golf & Country Club", region: "Delhi NCR", lat: 28.3672, lng: 77.1019 },
  { name: "Hamoni Golf", region: "Delhi NCR", lat: 28.2467, lng: 77.0641 },
  { name: "Noida Golf Course", region: "Delhi NCR", lat: 28.5703, lng: 77.3236 },
  { name: "Chandigarh Golf Club", region: "Chandigarh", lat: 30.7333, lng: 76.7794 },
  { name: "CGA Range Chandigarh", region: "Chandigarh", lat: 30.7415, lng: 76.7681 },
  { name: "Panchkula Golf Club", region: "Panchkula", lat: 30.6916, lng: 76.8534 },
  { name: "Rambagh Golf Club", region: "Jaipur", lat: 26.88, lng: 75.8104 },
  { name: "Ooty Gymkhana Club", region: "Ooty", lat: 11.4102, lng: 76.695 },
];

const bounds = [
  [11.0, 75.5],
  [31.0, 77.7],
];

const LocationsMap = () => {
  return (
    <div
      data-testid="locations-map"
      className="relative w-full h-[500px] md:h-[620px] hairline shadow-luxe overflow-hidden bg-forest-900 map-frame"
    >
      <MapContainer
        bounds={bounds}
        scrollWheelZoom={false}
        zoomControl={true}
        attributionControl={false}
        className="w-full h-full sat-map"
        style={{ background: "#0E1A14" }}
      >
        {/* Esri satellite imagery */}
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />
        {/* Esri reference labels (place names) */}
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
        />

        {venues.map((v) => (
          <React.Fragment key={v.name}>
            {/* Halo */}
            <CircleMarker
              center={[v.lat, v.lng]}
              radius={18}
              pathOptions={{
                color: "#C4A348",
                weight: 1,
                opacity: 0.4,
                fillColor: "#C4A348",
                fillOpacity: 0.1,
                interactive: false,
              }}
            />
            {/* Pin */}
            <CircleMarker
              center={[v.lat, v.lng]}
              radius={7}
              pathOptions={{
                color: "#F7F4ED",
                weight: 2,
                fillColor: "#C4A348",
                fillOpacity: 1,
              }}
            >
              <Tooltip
                direction="top"
                offset={[0, -10]}
                opacity={1}
                className="venue-tooltip"
              >
                <div style={{ fontFamily: "Outfit, sans-serif", fontSize: 12 }}>
                  <div style={{ fontWeight: 500, color: "#1A2820" }}>{v.name}</div>
                  <div
                    style={{
                      color: "#9C7E1F",
                      fontSize: 10,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      marginTop: 3,
                    }}
                  >
                    {v.region}
                  </div>
                </div>
              </Tooltip>
            </CircleMarker>
          </React.Fragment>
        ))}
      </MapContainer>

      {/* Premium duotone overlay over satellite */}
      <div className="pointer-events-none absolute inset-0 mix-blend-multiply opacity-[0.32]"
           style={{ background: "linear-gradient(135deg, rgba(26,40,32,0.55), rgba(212,175,55,0.18) 45%, rgba(26,40,32,0.55))" }} />

      {/* Vignette */}
      <div className="pointer-events-none absolute inset-0"
           style={{ boxShadow: "inset 0 0 160px 40px rgba(14,26,20,0.7)" }} />

      {/* Inner gold border */}
      <div className="pointer-events-none absolute inset-3 border border-brass/25" />

      {/* Corner ornaments (gold) */}
      <span className="pointer-events-none absolute top-3 left-3 w-8 h-px bg-brass" />
      <span className="pointer-events-none absolute top-3 left-3 w-px h-8 bg-brass" />
      <span className="pointer-events-none absolute top-3 right-3 w-8 h-px bg-brass" />
      <span className="pointer-events-none absolute top-3 right-3 w-px h-8 bg-brass" />
      <span className="pointer-events-none absolute bottom-3 left-3 w-8 h-px bg-brass" />
      <span className="pointer-events-none absolute bottom-3 left-3 w-px h-8 bg-brass" />
      <span className="pointer-events-none absolute bottom-3 right-3 w-8 h-px bg-brass" />
      <span className="pointer-events-none absolute bottom-3 right-3 w-px h-8 bg-brass" />

      {/* Legend card — top-left (dark glass) */}
      <div
        className="absolute top-7 left-7 hairline shadow-luxe bg-forest-900/85 backdrop-blur px-6 py-5 z-[400] border-brass/30"
        data-testid="map-legend"
      >
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-brass rounded-full shadow-[0_0_0_4px_rgba(196,163,72,0.22)]" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-brass">
            Network · Satellite
          </span>
        </div>
        <div className="font-serif text-3xl text-ivory mt-2 font-light leading-none">
          {venues.length} <span className="italic text-brass text-2xl">venues</span>
        </div>
        <div className="mt-3 gold-divider w-full" />
        <div className="text-[11px] text-ivory/70 mt-3 leading-relaxed">
          Delhi NCR · Chandigarh · Panchkula
          <br />
          Jaipur · Ooty
        </div>
      </div>

      {/* Compass card — bottom-right (dark glass) */}
      <div
        className="absolute bottom-7 right-7 hairline bg-forest-900/85 backdrop-blur px-4 py-3 z-[400] border-brass/30"
        aria-hidden
      >
        <div className="text-[9px] tracking-[0.3em] uppercase text-brass text-center">
          N
        </div>
        <div className="relative mx-auto w-12 h-12 mt-1">
          <span className="absolute inset-0 rounded-full border border-brass/55" />
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-full bg-brass/40" />
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-px w-full bg-brass/40" />
          <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-0.5 w-2 h-2 rotate-45 bg-brass" />
        </div>
        <div className="text-[9px] tracking-[0.25em] uppercase text-ivory/70 text-center mt-1">
          MMXXVI
        </div>
      </div>
    </div>
  );
};

export default LocationsMap;
